import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Container, Paper, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { SnackbarProvider, closeSnackbar } from 'notistack';
import { Routes, Route, Outlet } from 'react-router';
import TopBar from '../components/Navigation/Navbar/TopBar';
import Sidebar from '../components/Navigation/Navbar/Sidebar/Sidebar';
import PremiumPlusSnackbar from '../components/UI/CustomSnackbar';
import Loading from './Loading';
import ErrorBoundary from '../components/ErrorBoundary';
// Install PWA Dialog
import DeviceDetector from 'device-detector-js';
import { detectTouchScreen } from '../utils/device';
import InstallPWADialog from '../components/UI/Dialogs/InstallPWA';
import { ExamProvider } from '../contexts/ExamContext';

const PREFIX = 'Layout';

const classes = {
	root: `${PREFIX}-root`,
	content: `${PREFIX}-content`,
	toolbar: `${PREFIX}-toolbar`
};

const StyledSnackbarProvider = styled(SnackbarProvider)(() => ({
	[`& .${classes.root}`]: {
		display: 'flex',
		minHeight: '100vh',
		borderRadius: 0
	}
}));

const StyledContentProvider = styled('main')(({ theme }) => ({
	overflowY: 'auto', // Ensure vertical scrollbar for content
	overflowX: 'hidden', // Prevent horizontal scrollbar
	flexGrow: 1,
	backgroundImage:
		theme.palette.mode === 'light'
			? 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(250,250,250,1) 100%);'
			: 'linear-gradient(180deg, rgba(19,66,111,1) 0%, rgba(10,44,75,1) 100%);'
}));

export default function Home({ toggleDarkMode }) {
	// Mobile Menu State
	const [mobileOpen, setMobileOpen] = useState(false);
	const [openInstallPWADialog, setOpenInstallPWADialog] = useState(false);
	const [displaySteps, setDisplaySteps] = useState(false);
	const [displayInstallPWAMessage, setDisplayInstallPWAMessage] = useState(false);
	const [displayInstallPWAButton, setDisplayInstallPWAButton] = useState(false);
	const [beforeInstallPrompt, setBeforeInstallPrompt] = useState(null);
	const myRef = useRef(null);

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	// install PWA logic for Android phones. This detectes the beforeinstallprompt
	// event which is fired only on android devices and consecuently triggers our
	// custom install prompt
	useEffect(() => {
		const hasTouchScreen = detectTouchScreen();
		const deviceDetector = new DeviceDetector();
		const userAgent = navigator.userAgent;
		const dvc = deviceDetector.parse(userAgent);

		if (hasTouchScreen) {
			setDisplayInstallPWAMessage(true);

			if (dvc.device.brand === 'Apple') {
				setDisplaySteps(true);
			}
		}
		window.addEventListener('beforeinstallprompt', eventHandler, errorHandler);

		function eventHandler(event) {
			const hasTouchScreen = detectTouchScreen();

			event.preventDefault();
			setBeforeInstallPrompt(event);

			if (hasTouchScreen) {
				const ignoreInstallPWA = localStorage.getItem('ignoreInstallPWA');

				if (ignoreInstallPWA) {
					const ignore = ignoreInstallPWA === 'true';
					if (!ignore) {
						setOpenInstallPWADialog(true);
					}
				} else {
					setOpenInstallPWADialog(true);
				}

				if (event != null) {
					setDisplayInstallPWAButton(true);
				}
				setDisplayInstallPWAMessage(true);
			}
		}

		function errorHandler(e) {
			console.log('error: ' + e);
		}

		window.addEventListener('appinstalled', () => {
			// Hide the app-provided install promotion
			setOpenInstallPWADialog(false);
			setDisplayInstallPWAMessage(false);

			// Clear the deferredPrompt so it can be garbage collected
			setBeforeInstallPrompt(null);
		});

		// Hide the app-provided install promotion
		if (window.matchMedia('(display-mode: standalone)').matches) {
			setOpenInstallPWADialog(false);
			setDisplayInstallPWAMessage(false);
		}
	});

	const installPWA = () => {
		if (beforeInstallPrompt) {
			beforeInstallPrompt.prompt();
		} else {
			setDisplaySteps(true);
			setOpenInstallPWADialog(true);
		}
	};

	return (
		<StyledSnackbarProvider
			maxSnack={3}
			autoHideDuration={2500}
			ref={myRef}
			Components={{
				premiumPlus: PremiumPlusSnackbar
			}}
			action={(snackbarId) => (
				<IconButton onClick={() => closeSnackbar(snackbarId)}>
					<ClearIcon sx={{ color: 'white' }} />
				</IconButton>
			)}
			iconVariant={{
				success: '✅ ',
				error: '✖️ ',
				warning: '⚠️ ',
				info: 'ℹ️ '
			}}
		>
			<Paper
				sx={{
					display: 'flex',
					minHeight: '100vh',
					borderRadius: 0,
					width: '101%'
				}}
			>
				<TopBar toggleDarkMode={toggleDarkMode} handleDrawerToggle={handleDrawerToggle} />
				<Sidebar mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} />
				<StyledContentProvider>
					<ErrorBoundary>
						<ExamProvider>
							<Suspense fallback={<Loading />}>
								<Container maxWidth="lg" sx={{ mt: 8, p: '16px' }}>
									<Outlet />
								</Container>
							</Suspense>
						</ExamProvider>
					</ErrorBoundary>
				</StyledContentProvider>
				<InstallPWADialog
					open={openInstallPWADialog}
					setOpen={setOpenInstallPWADialog}
					title="Adaugă aplicația pe dispozitivul tău"
					description="Instalați aplicația pe dispozitiv pentru a o accesa cu ușurință
					oricând. Fără magazin de aplicații. Fără descărcare. Fara bătaie de cap. <br>"
					firstStep={`1: Asigurați-vă ca aplicația este deschisă in Safari`}
					secondStep={`2: Apasă pe`}
					thirdStep={`3: Selectează`}
					displaySteps={displaySteps}
					installPWA={installPWA}
					displayInstallPWA={displayInstallPWAButton}
				></InstallPWADialog>
			</Paper>
		</StyledSnackbarProvider>
	);
}
